import Phaser from 'phaser';
import { BiomeId } from '../systems/SaveSystem';
import {
  LevelData,
  LevelStorage,
  DecorationData,
  PlatformData,
  BlockData,
} from '../systems/LevelData';

// === Палитра ===
type DecorEntry = { kind: 'decor'; key: string; label: string; defaultScale: number };
type PlatformEntry = { kind: 'platform'; label: string; defaultW: number; defaultH: number };
type BlockEntry = {
  kind: 'block';
  label: string;
  defaultW: number;
  defaultH: number;
  texture: string;
};
type PaletteEntry = DecorEntry | PlatformEntry | BlockEntry;

const PALETTE: Record<BiomeId, PaletteEntry[]> = {
  biome1: [
    { kind: 'platform', label: 'platform', defaultW: 200, defaultH: 32 },
    { kind: 'block', label: 'wall', defaultW: 32, defaultH: 200, texture: 'biome1-wall' },
    { kind: 'block', label: 'block', defaultW: 100, defaultH: 100, texture: 'biome1-wall' },
    { kind: 'decor', key: 'decor-b1-rubble', label: 'rubble-1', defaultScale: 0.10 },
    { kind: 'decor', key: 'decor-b1-rubble-2', label: 'rubble-2', defaultScale: 0.10 },
    { kind: 'decor', key: 'decor-b1-rubble-3', label: 'rubble-3', defaultScale: 0.10 },
    { kind: 'decor', key: 'decor-b1-rubble-4', label: 'rubble-4', defaultScale: 0.10 },
    { kind: 'decor', key: 'decor-b1-rubble-5', label: 'rubble-5', defaultScale: 0.10 },
    { kind: 'decor', key: 'decor-b1-rubble-6', label: 'rubble-6', defaultScale: 0.10 },
    { kind: 'decor', key: 'decor-b1-rubble-7', label: 'rubble-7', defaultScale: 0.10 },
    { kind: 'decor', key: 'decor-b1-rubble-8', label: 'rubble-8', defaultScale: 0.10 },
    { kind: 'decor', key: 'decor-b1-rubble-9', label: 'rubble-9', defaultScale: 0.10 },
    { kind: 'decor', key: 'decor-b1-tombstone', label: 'tomb-1', defaultScale: 0.11 },
    { kind: 'decor', key: 'decor-b1-tombstone-2', label: 'tomb-2', defaultScale: 0.11 },
    { kind: 'decor', key: 'decor-b1-tombstone-3', label: 'tomb-3', defaultScale: 0.11 },
    { kind: 'decor', key: 'decor-b1-tombstone-4', label: 'tomb-4', defaultScale: 0.11 },
    { kind: 'decor', key: 'decor-b1-tombstone-5', label: 'tomb-5', defaultScale: 0.11 },
    { kind: 'decor', key: 'decor-b1-stone-block', label: 'stone-blk', defaultScale: 0.13 },
    { kind: 'decor', key: 'decor-b1-banner', label: 'banner', defaultScale: 0.16 },
    { kind: 'decor', key: 'decor-b1-lantern', label: 'lantern', defaultScale: 0.13 },
  ],
  biome2: [
    { kind: 'platform', label: 'platform', defaultW: 200, defaultH: 32 },
    { kind: 'block', label: 'wall', defaultW: 32, defaultH: 200, texture: 'biome2-wall' },
    { kind: 'block', label: 'block', defaultW: 100, defaultH: 100, texture: 'biome2-wall' },
    { kind: 'decor', key: 'decor-b2-mushroom', label: 'mush-1', defaultScale: 0.10 },
    { kind: 'decor', key: 'decor-b2-mushroom-2', label: 'mush-2', defaultScale: 0.10 },
    { kind: 'decor', key: 'decor-b2-mushroom-3', label: 'mush-3', defaultScale: 0.10 },
    { kind: 'decor', key: 'decor-b2-mushroom-4', label: 'mush-4', defaultScale: 0.10 },
    { kind: 'decor', key: 'decor-b2-crystal-cluster', label: 'cryst-1', defaultScale: 0.11 },
    { kind: 'decor', key: 'decor-b2-crystal-cluster-2', label: 'cryst-2', defaultScale: 0.11 },
    { kind: 'decor', key: 'decor-b2-crystal-cluster-3', label: 'cryst-3', defaultScale: 0.11 },
    { kind: 'decor', key: 'decor-b2-crystal-cluster-4', label: 'cryst-4', defaultScale: 0.11 },
    { kind: 'decor', key: 'decor-b2-stalagmite', label: 'stala', defaultScale: 0.13 },
    { kind: 'decor', key: 'decor-b2-crystal-pillar', label: 'pillar', defaultScale: 0.14 },
    { kind: 'decor', key: 'decor-b2-puddle', label: 'puddle', defaultScale: 0.10 },
    { kind: 'decor', key: 'decor-b2-bones', label: 'bones', defaultScale: 0.11 },
  ],
};

const PLACEMENT_LAYER_BASE = 5;
const PLATFORM_DEPTH = 0;
const BLOCK_DEPTH = -1;

// Hardcoded fallback геометрия — для предзаполнения редактора.
const BIOME1_PLATFORMS: PlatformData[] = [
  { cx: 500, cy: 2768, w: 300, h: 32 }, { cx: 900, cy: 2700, w: 200, h: 32 },
  { cx: 1200, cy: 2620, w: 180, h: 32 }, { cx: 1620, cy: 2620, w: 200, h: 32 },
  { cx: 1980, cy: 2540, w: 180, h: 32 }, { cx: 2270, cy: 2460, w: 180, h: 32 },
  { cx: 2455, cy: 2640, w: 100, h: 32 },
  { cx: 2500, cy: 2300, w: 140, h: 32 }, { cx: 2150, cy: 2150, w: 140, h: 32 },
  { cx: 2500, cy: 2000, w: 140, h: 32 }, { cx: 2150, cy: 1850, w: 140, h: 32 },
  { cx: 2500, cy: 1700, w: 140, h: 32 }, { cx: 2150, cy: 1550, w: 140, h: 32 },
  { cx: 2500, cy: 1400, w: 140, h: 32 },
  { cx: 2900, cy: 1380, w: 200, h: 32 }, { cx: 3300, cy: 1380, w: 200, h: 32 },
  { cx: 3700, cy: 1320, w: 200, h: 32 }, { cx: 4100, cy: 1320, w: 200, h: 32 },
  { cx: 4500, cy: 1260, w: 200, h: 32 },
  { cx: 4900, cy: 1110, w: 180, h: 32 }, { cx: 5200, cy: 960, w: 180, h: 32 },
  { cx: 5500, cy: 810, w: 180, h: 32 }, { cx: 5800, cy: 660, w: 180, h: 32 },
  { cx: 6200, cy: 510, w: 220, h: 32 }, { cx: 6500, cy: 380, w: 200, h: 32 },
  { cx: 6800, cy: 250, w: 200, h: 32 }, { cx: 7200, cy: 250, w: 220, h: 32 },
  { cx: 7700, cy: 320, w: 220, h: 32 }, { cx: 8200, cy: 400, w: 220, h: 32 },
  { cx: 8800, cy: 450, w: 250, h: 32 },
  { cx: 3900, cy: 1480, w: 180, h: 32 }, { cx: 4100, cy: 1660, w: 180, h: 32 },
  { cx: 4000, cy: 1900, w: 220, h: 32 },
  { cx: 9100, cy: 380, w: 200, h: 32 }, { cx: 9400, cy: 320, w: 200, h: 32 },
  { cx: 9700, cy: 280, w: 220, h: 32 },
];

const BIOME1_BLOCKS: BlockData[] = [
  { cx: 5120, cy: 2864, w: 10240, h: 32, texture: 'biome1-wall' }, // floor
  { cx: 16, cy: 1524, w: 32, h: 2648, texture: 'biome1-wall' },     // left wall
  { cx: 10224, cy: 1524, w: 32, h: 2648, texture: 'biome1-wall' },  // right wall
];

const BIOME2_PLATFORMS: PlatformData[] = [
  { cx: 400, cy: 2200, w: 200, h: 32 }, { cx: 800, cy: 2060, w: 200, h: 32 },
  { cx: 1200, cy: 1960, w: 220, h: 32 }, { cx: 1600, cy: 1900, w: 220, h: 32 },
  { cx: 2050, cy: 1950, w: 180, h: 32 },
  { cx: 2380, cy: 1960, w: 220, h: 32 }, { cx: 2700, cy: 1820, w: 200, h: 32 },
  { cx: 3000, cy: 1640, w: 200, h: 32 }, { cx: 3250, cy: 1460, w: 200, h: 32 },
  { cx: 3500, cy: 1326, w: 200, h: 32 },
  { cx: 3870, cy: 1460, w: 200, h: 32 }, { cx: 4150, cy: 1640, w: 200, h: 32 },
  { cx: 4400, cy: 1840, w: 200, h: 32 }, { cx: 4650, cy: 2050, w: 220, h: 32 },
  { cx: 4900, cy: 2200, w: 180, h: 32 },
  { cx: 3100, cy: 1130, w: 180, h: 32 }, { cx: 2900, cy: 940, w: 220, h: 32 },
  { cx: 3850, cy: 1240, w: 200, h: 32 }, { cx: 4050, cy: 1040, w: 220, h: 32 },
];

const BIOME2_BLOCKS: BlockData[] = [
  { cx: 2560, cy: 2384, w: 5120, h: 32, texture: 'biome2-wall' },  // floor
  { cx: 2560, cy: 16, w: 5120, h: 32, texture: 'biome2-wall' },    // ceiling
  { cx: 16, cy: 1200, w: 32, h: 2336, texture: 'biome2-wall' },    // left
  { cx: 5104, cy: 1200, w: 32, h: 2336, texture: 'biome2-wall' },  // right
  { cx: 2200, cy: 906, w: 64, h: 1748, texture: 'biome2-wall' },   // div1 top
  { cx: 2200, cy: 2214, w: 64, h: 308, texture: 'biome2-wall' },   // div1 bottom
  { cx: 3700, cy: 566, w: 64, h: 1068, texture: 'biome2-wall' },   // div2 top
  { cx: 3700, cy: 1874, w: 64, h: 988, texture: 'biome2-wall' },   // div2 bottom
];

// === Editor item — discriminated union ===
type EditorItem =
  | {
      kind: 'decor';
      sprite: Phaser.GameObjects.Image;
      key: string;
      scale: number;
      layer: number;
    }
  | {
      kind: 'platform';
      rect: Phaser.GameObjects.Rectangle;
    }
  | {
      kind: 'block';
      rect: Phaser.GameObjects.Rectangle;
      texture: string;
    };

function itemBounds(item: EditorItem): { x: number; y: number; w: number; h: number } {
  if (item.kind === 'decor') {
    const s = item.sprite;
    // origin (0.5, 1) — bbox от (s.x - w/2, s.y - h) до (s.x + w/2, s.y)
    return { x: s.x, y: s.y - s.displayHeight / 2, w: s.displayWidth, h: s.displayHeight };
  } else {
    const r = item.rect;
    return { x: r.x, y: r.y, w: r.width, h: r.height };
  }
}

function itemPos(item: EditorItem): { x: number; y: number } {
  if (item.kind === 'decor') return { x: item.sprite.x, y: item.sprite.y };
  return { x: item.rect.x, y: item.rect.y };
}

function setItemPos(item: EditorItem, x: number, y: number) {
  if (item.kind === 'decor') item.sprite.setPosition(x, y);
  else item.rect.setPosition(x, y);
}

export class EditorScene extends Phaser.Scene {
  private biomeId: BiomeId = 'biome1';
  private worldWidth = 10240;
  private worldHeight = 2880;

  private items: EditorItem[] = [];
  private selectedItem: EditorItem | null = null;
  private currentPaletteIndex = 0;

  private selectionRect!: Phaser.GameObjects.Rectangle;
  private hudText!: Phaser.GameObjects.Text;
  private paletteContainer!: Phaser.GameObjects.Container;
  private paletteHighlight!: Phaser.GameObjects.Rectangle;

  // Слои разделения мира и UI: мир зумится главной камерой, UI остаётся
  // фиксированного размера на отдельной камере (она не зумится).
  private worldLayer!: Phaser.GameObjects.Layer;
  private uiLayer!: Phaser.GameObjects.Layer;
  private uiCam!: Phaser.Cameras.Scene2D.Camera;

  private dragging = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;

  private cameraSpeed = 600;

  constructor() {
    super('EditorScene');
  }

  init(data: { biomeId?: BiomeId }) {
    this.biomeId = data?.biomeId ?? 'biome1';
    this.items = [];
    this.selectedItem = null;
    this.currentPaletteIndex = 0;
    this.dragging = false;
  }

  create() {
    if (this.biomeId === 'biome1') {
      this.worldWidth = 10240;
      this.worldHeight = 2880;
    } else {
      this.worldWidth = 5120;
      this.worldHeight = 2400;
    }

    const cam = this.cameras.main;
    cam.setBounds(0, 0, this.worldWidth, this.worldHeight);
    cam.setBackgroundColor('#0a0d12');

    // === Two-camera setup ===
    // mainCam (this.cameras.main) — зумится, рендерит мир
    // uiCam — фикс zoom=1, рендерит интерфейс редактора (палитра, HUD)
    this.worldLayer = this.add.layer();
    this.uiLayer = this.add.layer();
    this.uiCam = this.cameras.add(0, 0, cam.width, cam.height);
    this.uiCam.setScroll(0, 0);
    this.uiCam.setZoom(1);
    cam.ignore(this.uiLayer);
    this.uiCam.ignore(this.worldLayer);

    // Подложка фон-картинками биома (только визуал, без параллакса)
    this.drawBiomeBackdrop();

    // Сетка координат
    const grid = this.add.graphics().setDepth(-90);
    grid.lineStyle(1, 0x2a3040, 0.25);
    for (let x = 0; x < this.worldWidth; x += 200) {
      grid.beginPath();
      grid.moveTo(x, 0);
      grid.lineTo(x, this.worldHeight);
      grid.strokePath();
    }
    for (let y = 0; y < this.worldHeight; y += 200) {
      grid.beginPath();
      grid.moveTo(0, y);
      grid.lineTo(this.worldWidth, y);
      grid.strokePath();
    }
    this.worldLayer.add(grid);

    // Загружаем существующий уровень или предзаполняем hardcoded геометрией
    const saved = LevelStorage.load(this.biomeId);
    if (saved && (saved.platforms.length > 0 || saved.blocks.length > 0 || saved.decorations.length > 0)) {
      for (const b of saved.blocks) this.addBlockItem(b.cx, b.cy, b.w, b.h, b.texture);
      for (const p of saved.platforms) this.addPlatformItem(p.cx, p.cy, p.w, p.h);
      for (const d of saved.decorations) this.addDecorItem(d.x, d.y, d.key, d.scale, d.layer);
    } else {
      this.populateFromHardcoded();
    }

    // Маркеры портала/телепорта (read-only визуал)
    this.drawBiomeMarkers();

    // Selection rectangle (в мире — следит за выделенным item-ом)
    this.selectionRect = this.add.rectangle(0, 0, 10, 10);
    this.selectionRect.setStrokeStyle(2, 0xffd060, 0.95);
    this.selectionRect.setFillStyle(0xffd060, 0.1);
    this.selectionRect.setVisible(false);
    this.selectionRect.setDepth(900);
    this.worldLayer.add(this.selectionRect);

    // HUD text — в UI-слое, не зумится
    this.hudText = this.add
      .text(12, 12, '', {
        font: '12px monospace',
        color: '#cce',
        backgroundColor: '#000a',
        padding: { x: 8, y: 6 },
      })
      .setDepth(2000);
    this.uiLayer.add(this.hudText);

    this.buildPalette();

    // Input
    this.input.on('pointerdown', this.onPointerDown, this);
    this.input.on('pointerup', this.onPointerUp, this);
    this.input.on('pointermove', this.onPointerMove, this);
    this.input.on(
      'wheel',
      (pointer: Phaser.Input.Pointer, _go: unknown, _dx: number, dy: number) => {
        this.zoomAtPointer(pointer, dy > 0 ? 1 / 1.15 : 1.15);
      },
    );
    this.input.keyboard?.on('keydown-DELETE', () => this.deleteSelected());
    this.input.keyboard?.on('keydown-BACKSPACE', () => this.deleteSelected());
    this.input.keyboard?.on('keydown-ESC', () => this.deselect());
    this.input.keyboard?.on('keydown-S', (e: KeyboardEvent) => {
      // Save только с модификатором — иначе S панит камеру вниз (WASD).
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      if (e.shiftKey) this.exportJson();
      else this.saveLevel();
    });
    this.input.keyboard?.on('keydown-L', () => this.loadLevel());
    this.input.keyboard?.on('keydown-C', () => this.clearAll());
    this.input.keyboard?.on('keydown-P', () => this.playLevel());
    this.input.keyboard?.on('keydown-R', () => this.resetZoom());
    this.input.keyboard?.on('keydown-DIGIT_0', () => this.fitToWorld());
    this.input.keyboard?.on('keydown-PLUS', () => this.scaleSelected(1.1));
    this.input.keyboard?.on('keydown-EQUALS', () => this.scaleSelected(1.1));
    this.input.keyboard?.on('keydown-MINUS', () => this.scaleSelected(1 / 1.1));
    this.input.keyboard?.on('keydown-CLOSED_BRACKET', () => this.layerSelected(1));
    this.input.keyboard?.on('keydown-OPEN_BRACKET', () => this.layerSelected(-1));
    this.input.keyboard?.on('keydown-F1', () => this.scene.start('GameScene', { biomeId: this.biomeId }));
    this.input.keyboard?.on('keydown-PAGE_UP', () => this.cyclePalette(-1));
    this.input.keyboard?.on('keydown-PAGE_DOWN', () => this.cyclePalette(1));
    this.input.keyboard?.on('keydown', (e: KeyboardEvent) => {
      if (e.key >= '1' && e.key <= '9') this.selectPaletteIndex(parseInt(e.key) - 1);
    });

    this.selectPaletteIndex(0);
    this.updateHud();
  }

  // ------------------------------------------------------------------
  // Подложка биома (визуал)
  // ------------------------------------------------------------------

  private drawBiomeBackdrop() {
    const ch = this.cameras.main.height;
    const bgKeys =
      this.biomeId === 'biome1'
        ? ['biome1-bg', 'biome1-bg-cathedral', 'biome1-bg-temple', 'biome1-bg-crypt']
        : ['biome2-bg-crystals', 'biome2-bg-flora', 'biome2-bg-lava'];
    const pieceW = (this.worldWidth / Math.max(1, bgKeys.length - 1)) * 1.2;
    for (let i = 0; i < bgKeys.length; i++) {
      const x = (i + 0.5) * (this.worldWidth / bgKeys.length);
      const bg = this.add.image(x, this.worldHeight - ch / 2, bgKeys[i]);
      bg.setScale(pieceW / bg.width);
      bg.setAlpha(0.45);
      bg.setDepth(-100);
      this.worldLayer.add(bg);
    }
  }

  private drawBiomeMarkers() {
    const H = this.worldHeight;
    const W = this.worldWidth;
    const g = this.add.graphics().setDepth(900);
    this.worldLayer.add(g);
    if (this.biomeId === 'biome1') {
      this.drawMarker(g, 200, H - 32, 'PORTAL', 0xc8a050);
      this.drawMarker(g, 8800, 434, 'TP→2', 0xff6aa8);
    } else {
      this.drawMarker(g, 240, H - 32, 'PORTAL', 0xc8a050);
      this.drawMarker(g, W - 200, H - 32, 'TP→1', 0x66cccc);
    }
  }

  private drawMarker(
    g: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    label: string,
    color: number,
  ) {
    g.fillStyle(color, 0.85);
    g.fillCircle(x, y - 12, 10);
    g.lineStyle(2, 0xffffff, 0.7);
    g.strokeCircle(x, y - 12, 10);
    const t = this.add
      .text(x, y - 28, label, {
        font: '11px monospace',
        color: '#ffffff',
        backgroundColor: '#000a',
        padding: { x: 4, y: 2 },
      })
      .setOrigin(0.5, 1)
      .setDepth(901);
    this.worldLayer.add(t);
  }

  // ------------------------------------------------------------------
  // Создание элементов разных типов
  // ------------------------------------------------------------------

  private addDecorItem(x: number, y: number, key: string, scale: number, layer: number): EditorItem {
    const sprite = this.add.image(x, y, key);
    sprite.setOrigin(0.5, 1);
    sprite.setScale(scale);
    sprite.setDepth(PLACEMENT_LAYER_BASE + layer * 0.001);
    this.worldLayer.add(sprite);
    const item: EditorItem = { kind: 'decor', sprite, key, scale, layer };
    this.items.push(item);
    return item;
  }

  private addPlatformItem(cx: number, cy: number, w: number, h: number): EditorItem {
    const r = this.add.rectangle(cx, cy, w, h, 0x2a3346, 0.85);
    r.setStrokeStyle(1, 0x6a7488, 0.85);
    r.setDepth(PLATFORM_DEPTH);
    this.worldLayer.add(r);
    const topEdge = this.add.rectangle(cx, cy - h / 2 + 1, w, 2, 0x9caebf, 0.7);
    topEdge.setDepth(PLATFORM_DEPTH);
    this.worldLayer.add(topEdge);
    r.setData('topEdge', topEdge);
    const item: EditorItem = { kind: 'platform', rect: r };
    this.items.push(item);
    return item;
  }

  private addBlockItem(cx: number, cy: number, w: number, h: number, texture: string): EditorItem {
    const r = this.add.rectangle(cx, cy, w, h, 0x1a2030, 0.9);
    r.setStrokeStyle(1, 0x4a5060, 0.7);
    r.setDepth(BLOCK_DEPTH);
    this.worldLayer.add(r);
    const item: EditorItem = { kind: 'block', rect: r, texture };
    this.items.push(item);
    return item;
  }

  private populateFromHardcoded() {
    const blocks = this.biomeId === 'biome1' ? BIOME1_BLOCKS : BIOME2_BLOCKS;
    const platforms = this.biomeId === 'biome1' ? BIOME1_PLATFORMS : BIOME2_PLATFORMS;
    for (const b of blocks) this.addBlockItem(b.cx, b.cy, b.w, b.h, b.texture);
    for (const p of platforms) this.addPlatformItem(p.cx, p.cy, p.w, p.h);
  }

  // ------------------------------------------------------------------
  // Палитра
  // ------------------------------------------------------------------

  private buildPalette() {
    const palette = PALETTE[this.biomeId];
    const cw = this.cameras.main.width;
    const ch = this.cameras.main.height;
    const slotSize = 48;
    const gap = 4;
    const totalW = palette.length * slotSize + (palette.length - 1) * gap;
    const startX = (cw - totalW) / 2;
    const slotY = ch - slotSize - 12;

    this.paletteContainer = this.add.container(0, 0);
    this.paletteContainer.setDepth(2000);
    this.uiLayer.add(this.paletteContainer);

    const bg = this.add.rectangle(cw / 2, slotY + slotSize / 2, totalW + 24, slotSize + 12, 0x000000, 0.7);
    bg.setStrokeStyle(1, 0x6a4828, 0.7);
    this.paletteContainer.add(bg);

    for (let i = 0; i < palette.length; i++) {
      const sx = startX + i * (slotSize + gap);
      const slot = this.add.rectangle(
        sx + slotSize / 2,
        slotY + slotSize / 2,
        slotSize,
        slotSize,
        0x1a2030,
        0.9,
      );
      slot.setStrokeStyle(1, 0x4a3a20);
      this.paletteContainer.add(slot);

      const entry = palette[i];
      if (entry.kind === 'decor') {
        const icon = this.add.image(sx + slotSize / 2, slotY + slotSize / 2, entry.key);
        const tex = this.textures.get(entry.key);
        const w = tex.source[0].width;
        const h = tex.source[0].height;
        const iconScale = Math.min((slotSize - 8) / w, (slotSize - 8) / h);
        icon.setScale(iconScale);
        icon.setInteractive({ useHandCursor: true });
        icon.on('pointerdown', (p: Phaser.Input.Pointer) => {
          p.event.stopPropagation();
          this.selectPaletteIndex(i);
        });
        this.paletteContainer.add(icon);
      } else {
        // Platform или block — рисуем стилизованную иконку
        const color = entry.kind === 'platform' ? 0x6a7488 : 0x4a5060;
        const iw = entry.kind === 'platform' ? slotSize - 12 : slotSize - 20;
        const ih = entry.kind === 'platform' ? 8 : slotSize - 20;
        const icon = this.add.rectangle(sx + slotSize / 2, slotY + slotSize / 2, iw, ih, color, 0.95);
        icon.setStrokeStyle(1, 0x9caebf);
        icon.setInteractive({ useHandCursor: true });
        icon.on('pointerdown', (p: Phaser.Input.Pointer) => {
          p.event.stopPropagation();
          this.selectPaletteIndex(i);
        });
        this.paletteContainer.add(icon);
      }

      const label = this.add.text(sx + slotSize / 2, slotY + slotSize + 2, palette[i].label, {
        font: '9px monospace',
        color: '#a8a8b0',
      });
      label.setOrigin(0.5, 0);
      this.paletteContainer.add(label);
    }

    this.paletteHighlight = this.add.rectangle(0, 0, slotSize + 4, slotSize + 4);
    this.paletteHighlight.setStrokeStyle(2, 0xffd060, 1);
    this.paletteHighlight.setFillStyle(0, 0);
    this.paletteHighlight.setDepth(2001);
    this.uiLayer.add(this.paletteHighlight);
  }

  private selectPaletteIndex(index: number) {
    const palette = PALETTE[this.biomeId];
    if (index < 0 || index >= palette.length) return;
    this.currentPaletteIndex = index;

    const cw = this.cameras.main.width;
    const ch = this.cameras.main.height;
    const slotSize = 48;
    const gap = 4;
    const totalW = palette.length * slotSize + (palette.length - 1) * gap;
    const startX = (cw - totalW) / 2;
    const slotY = ch - slotSize - 12;
    const sx = startX + index * (slotSize + gap);
    this.paletteHighlight.setPosition(sx + slotSize / 2, slotY + slotSize / 2);
    this.updateHud();
  }

  private cyclePalette(dir: number) {
    const palette = PALETTE[this.biomeId];
    const next = (this.currentPaletteIndex + dir + palette.length) % palette.length;
    this.selectPaletteIndex(next);
  }

  // ------------------------------------------------------------------
  // Зум
  // ------------------------------------------------------------------

  private zoomAtPointer(pointer: Phaser.Input.Pointer, mul: number) {
    const cam = this.cameras.main;
    const oldZoom = cam.zoom;
    const newZoom = Phaser.Math.Clamp(oldZoom * mul, 0.05, 2);
    if (newZoom === oldZoom) return;
    const wx = pointer.worldX;
    const wy = pointer.worldY;
    cam.setZoom(newZoom);
    cam.scrollX = wx - pointer.x / newZoom;
    cam.scrollY = wy - pointer.y / newZoom;
    this.updateHud();
  }

  private resetZoom() {
    this.cameras.main.setZoom(1);
    this.updateHud();
  }

  private fitToWorld() {
    const cam = this.cameras.main;
    const z = Math.min(cam.width / this.worldWidth, cam.height / this.worldHeight) * 0.95;
    cam.setZoom(z);
    cam.scrollX = (this.worldWidth - cam.width / z) / 2;
    cam.scrollY = (this.worldHeight - cam.height / z) / 2;
    this.updateHud();
  }

  // ------------------------------------------------------------------
  // Mouse / Click handling
  // ------------------------------------------------------------------

  private onPointerDown(pointer: Phaser.Input.Pointer) {
    if (pointer.y > this.cameras.main.height - 80) return;

    const wx = pointer.worldX;
    const wy = pointer.worldY;

    const hit = this.findItemAt(wx, wy);
    if (hit) {
      this.selectItem(hit);
      this.dragging = true;
      const pos = itemPos(hit);
      this.dragOffsetX = pos.x - wx;
      this.dragOffsetY = pos.y - wy;
      return;
    }

    // Размещение нового элемента согласно текущей кисти
    const entry = PALETTE[this.biomeId][this.currentPaletteIndex];
    if (!entry) return;
    let item: EditorItem;
    if (entry.kind === 'decor') {
      item = this.addDecorItem(wx, wy, entry.key, entry.defaultScale, this.items.length);
    } else if (entry.kind === 'platform') {
      item = this.addPlatformItem(wx, wy, entry.defaultW, entry.defaultH);
    } else {
      item = this.addBlockItem(wx, wy, entry.defaultW, entry.defaultH, entry.texture);
    }
    this.selectItem(item);
  }

  private onPointerMove(pointer: Phaser.Input.Pointer) {
    if (!this.dragging || !this.selectedItem) return;
    const newX = pointer.worldX + this.dragOffsetX;
    const newY = pointer.worldY + this.dragOffsetY;
    setItemPos(this.selectedItem, newX, newY);
    // Обновляем topEdge для платформ
    if (this.selectedItem.kind === 'platform') {
      const topEdge = this.selectedItem.rect.getData('topEdge') as Phaser.GameObjects.Rectangle;
      if (topEdge) topEdge.setPosition(newX, newY - this.selectedItem.rect.height / 2 + 1);
    }
    this.updateSelectionRect();
  }

  private onPointerUp() {
    this.dragging = false;
  }

  private findItemAt(x: number, y: number): EditorItem | null {
    for (let i = this.items.length - 1; i >= 0; i--) {
      const it = this.items[i];
      const b = itemBounds(it);
      if (x >= b.x - b.w / 2 && x <= b.x + b.w / 2 && y >= b.y - b.h / 2 && y <= b.y + b.h / 2) {
        return it;
      }
    }
    return null;
  }

  private selectItem(item: EditorItem) {
    this.selectedItem = item;
    this.updateSelectionRect();
    this.updateHud();
  }

  private deselect() {
    this.selectedItem = null;
    this.selectionRect.setVisible(false);
    this.updateHud();
  }

  private updateSelectionRect() {
    if (!this.selectedItem) {
      this.selectionRect.setVisible(false);
      return;
    }
    const b = itemBounds(this.selectedItem);
    this.selectionRect.setPosition(b.x, b.y);
    this.selectionRect.setSize(b.w, b.h);
    this.selectionRect.setVisible(true);
  }

  private deleteSelected() {
    if (!this.selectedItem) return;
    const idx = this.items.indexOf(this.selectedItem);
    if (idx >= 0) {
      if (this.selectedItem.kind === 'decor') {
        this.selectedItem.sprite.destroy();
      } else {
        const topEdge = this.selectedItem.rect.getData('topEdge') as Phaser.GameObjects.Rectangle | undefined;
        if (topEdge) topEdge.destroy();
        this.selectedItem.rect.destroy();
      }
      this.items.splice(idx, 1);
    }
    this.deselect();
  }

  private scaleSelected(mul: number) {
    if (!this.selectedItem) return;
    if (this.selectedItem.kind === 'decor') {
      this.selectedItem.scale *= mul;
      this.selectedItem.sprite.setScale(this.selectedItem.scale);
    } else {
      // Для платформы/блока — масштабируем ширину
      const r = this.selectedItem.rect;
      const newW = Math.max(16, Math.round(r.width * mul));
      r.setSize(newW, r.height);
      // Обновим topEdge
      const topEdge = r.getData('topEdge') as Phaser.GameObjects.Rectangle | undefined;
      if (topEdge) topEdge.setSize(newW, 2);
    }
    this.updateSelectionRect();
    this.updateHud();
  }

  private layerSelected(delta: number) {
    if (!this.selectedItem || this.selectedItem.kind !== 'decor') return;
    this.selectedItem.layer += delta;
    this.selectedItem.sprite.setDepth(PLACEMENT_LAYER_BASE + this.selectedItem.layer * 0.001);
    this.updateHud();
  }

  // ------------------------------------------------------------------
  // Save / Load / Play
  // ------------------------------------------------------------------

  private buildLevelData(): LevelData {
    const decorations: DecorationData[] = [];
    const platforms: PlatformData[] = [];
    const blocks: BlockData[] = [];
    for (const it of this.items) {
      if (it.kind === 'decor') {
        decorations.push({
          x: it.sprite.x,
          y: it.sprite.y,
          key: it.key,
          scale: it.scale,
          layer: it.layer,
        });
      } else if (it.kind === 'platform') {
        platforms.push({
          cx: it.rect.x,
          cy: it.rect.y,
          w: it.rect.width,
          h: it.rect.height,
        });
      } else {
        blocks.push({
          cx: it.rect.x,
          cy: it.rect.y,
          w: it.rect.width,
          h: it.rect.height,
          texture: it.texture,
        });
      }
    }
    return { biomeId: this.biomeId, version: 2, decorations, platforms, blocks };
  }

  private saveLevel() {
    const data = this.buildLevelData();
    LevelStorage.save(data);
    this.flashStatus(
      `Saved: ${data.platforms.length}p + ${data.blocks.length}b + ${data.decorations.length}d`,
    );
  }

  private loadLevel() {
    const data = LevelStorage.load(this.biomeId);
    if (!data) {
      this.flashStatus('No saved level');
      return;
    }
    this.clearAllSilent();
    for (const b of data.blocks) this.addBlockItem(b.cx, b.cy, b.w, b.h, b.texture);
    for (const p of data.platforms) this.addPlatformItem(p.cx, p.cy, p.w, p.h);
    for (const d of data.decorations) this.addDecorItem(d.x, d.y, d.key, d.scale, d.layer);
    this.flashStatus(
      `Loaded: ${data.platforms.length}p + ${data.blocks.length}b + ${data.decorations.length}d`,
    );
  }

  private clearAllSilent() {
    for (const it of this.items) {
      if (it.kind === 'decor') it.sprite.destroy();
      else {
        const topEdge = it.rect.getData('topEdge') as Phaser.GameObjects.Rectangle | undefined;
        if (topEdge) topEdge.destroy();
        it.rect.destroy();
      }
    }
    this.items = [];
    this.deselect();
  }

  private clearAll() {
    if (this.items.length === 0) return;
    this.clearAllSilent();
    this.flashStatus('Cleared');
  }

  private exportJson() {
    const data = this.buildLevelData();
    const json = LevelStorage.toJson(data);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `level-${this.biomeId}.json`;
    a.click();
    URL.revokeObjectURL(url);
    this.flashStatus('Exported JSON');
  }

  private playLevel() {
    this.saveLevel();
    this.scene.start('GameScene', { biomeId: this.biomeId });
  }

  private flashStatus(msg: string) {
    const txt = this.add
      .text(this.cameras.main.width / 2, 60, msg, {
        font: '14px monospace',
        color: '#ffd060',
        backgroundColor: '#000a',
        padding: { x: 12, y: 6 },
      })
      .setOrigin(0.5, 0)
      .setDepth(2100);
    this.uiLayer.add(txt);
    this.tweens.add({
      targets: txt,
      alpha: 0,
      y: 40,
      duration: 1500,
      delay: 800,
      onComplete: () => txt.destroy(),
    });
  }

  private updateHud() {
    const palette = PALETTE[this.biomeId];
    const cur = palette[this.currentPaletteIndex];
    const sel = this.selectedItem;
    const zoom = this.cameras.main.zoom;

    let selLine = 'Selected: (none)';
    if (sel) {
      const p = itemPos(sel);
      if (sel.kind === 'decor') {
        selLine = `Selected: decor ${sel.key}  scale=${sel.scale.toFixed(3)} layer=${sel.layer}  pos=${Math.round(p.x)},${Math.round(p.y)}`;
      } else if (sel.kind === 'platform') {
        selLine = `Selected: PLATFORM  ${Math.round(sel.rect.width)}×${Math.round(sel.rect.height)}  pos=${Math.round(p.x)},${Math.round(p.y)}`;
      } else {
        selLine = `Selected: BLOCK ${sel.texture}  ${Math.round(sel.rect.width)}×${Math.round(sel.rect.height)}  pos=${Math.round(p.x)},${Math.round(p.y)}`;
      }
    }

    const counts = this.items.reduce(
      (acc, it) => {
        if (it.kind === 'decor') acc.d++;
        else if (it.kind === 'platform') acc.p++;
        else acc.b++;
        return acc;
      },
      { d: 0, p: 0, b: 0 },
    );

    const lines = [
      `EDITOR — biome: ${this.biomeId}  zoom: ${(zoom * 100).toFixed(0)}%`,
      `Brush: [${this.currentPaletteIndex + 1}/${palette.length}] ${cur?.label ?? '-'} (${cur?.kind ?? '-'})`,
      selLine,
      `Items: ${counts.p} platforms + ${counts.b} blocks + ${counts.d} decorations`,
      '',
      'Click empty: place brush | Click item: select | Drag: move',
      'WASD/arrows: pan | Wheel: zoom | R: zoom 100% | 0: fit world',
      '1-9 / PgUp/PgDn: brush | +/-: scale (decor) or width (plat/block)',
      '[/]: layer (decor) | Del: remove | Esc: deselect',
      'Ctrl+S: save | Ctrl+Shift+S: export JSON | L: load | C: clear all',
      'P: play (saves & runs) | F1: back to game',
    ];
    this.hudText.setText(lines.join('\n'));
  }

  override update(_time: number, delta: number) {
    const cam = this.cameras.main;
    const dt = delta / 1000;
    const speed = this.cameraSpeed / cam.zoom;
    const kb = this.input.keyboard;
    if (!kb) return;
    if (kb.checkDown(kb.addKey('A')) || kb.checkDown(kb.addKey('LEFT'))) cam.scrollX -= speed * dt;
    if (kb.checkDown(kb.addKey('D')) || kb.checkDown(kb.addKey('RIGHT'))) cam.scrollX += speed * dt;
    if (kb.checkDown(kb.addKey('W')) || kb.checkDown(kb.addKey('UP'))) cam.scrollY -= speed * dt;
    if (kb.checkDown(kb.addKey('S')) || kb.checkDown(kb.addKey('DOWN'))) cam.scrollY += speed * dt;
  }
}
